/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import { Component, Inject, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { BULB_BUSY_COVER_SERVICE } from '../angularjs.providers';
import { PageSnapshotDialogService } from './page-snapshot-dialog.service';
import { TranslationService } from '../../common/translation.service';

/**
 * Component that loads the page snapshot component.
 */
@Component({
  selector: 'page-snapshot-dialog',
  templateUrl: './page-snapshot-dialog.component.html',
  standalone: false,
})
export class PageSnapshotDialogComponent implements OnInit {
  /**
   * The current page the paginator is on.
   */
  public currentPage : number;

  /**
   * The list of snapshots for the current page.
   */
  public currentSnapshots : any[];

  /**
   * The id of the draft unit we are viewing the snapshots of.
   */
  public draftPageId : string;

  /**
   * If we are currently doing a fetch during lazy loading.
   */
  public loading: boolean;

  /**
   * Array of translated month names.
   */
  public months;

  /**
   * The length of pages we want to display.
   */
  public pageSize = 7;

  /**
   * Shows the paginator only if there is more than one page.
   */
  public showPaginator = false;

  /**
   * The total number of pages of snap shots.
   */
  public totalPages: number;

  /**
   * The total number of snapshots.
   */
  public totalSnapshots: number;

  /**
   * The id of the current unit we are viewing the snapshots of.
   */
  public unitId : string;

  /**
   * This is the Index value of the current snapshot selected.
   */
  public selectedIndex : number;

  /**
   * Default Constructor.
   *
   * @param dynamicDialogRef Reference to the open dialog.
   * @param dialogConfig Dialog configuration.
   * @param pageSnapshotDialogService
   * @param $translate service.
   * @param bulbBusyCoverService
   * @param state
   */
  constructor(
    public dynamicDialogRef : DynamicDialogRef,
    public dialogConfig: DynamicDialogConfig,
    private pageSnapshotDialogService : PageSnapshotDialogService,
    private $translate : TranslationService,
    @Inject(BULB_BUSY_COVER_SERVICE) private bulbBusyCoverService,
    @Inject('$state') private state,
  ) {}

  /**
   * Lifecycle method called after component initialized all data-bound properties.
   * Used to set initial page data.
   */
  ngOnInit() {
    this.months = this.$translate.translateArrayInstant([
      'aero_calendar_month_name_january',
      'aero_calendar_month_name_february',
      'aero_calendar_month_name_march',
      'aero_calendar_month_name_april',
      'aero_calendar_month_name_may',
      'aero_calendar_month_name_june',
      'aero_calendar_month_name_july',
      'aero_calendar_month_name_august',
      'aero_calendar_month_name_september',
      'aero_calendar_month_name_october',
      'aero_calendar_month_name_november',
      'aero_calendar_month_name_december',
    ]);
    // @ts-ignore
    this.unitId = this.dialogConfig.unitId;
    // @ts-ignore
    this.draftPageId = this.dialogConfig.draftPageId;
    this.fetchSnapshotPage(0);
    window.addEventListener('changeSnapshot', this.changeSnapshot);
  }

  /**
   * Fetches a single page of snapshots.
   *
   * @param pageNumber the page we want to fetch.
   */
  fetchSnapshotPage(pageNumber : number) : void {
    this.loading = true;
    const params = { page: pageNumber, size: this.pageSize };
    this.bulbBusyCoverService.showBusyDuring(
      this.pageSnapshotDialogService.getSnapshotPage(this.draftPageId, params)
        .then((response) => {
          this.currentPage = pageNumber;
          this.currentSnapshots = this.formatSnapshots(response.dtos);
          this.totalPages = response.totalPages;
          // Doesn't need to be exact, used in paginator to display page numbers and jump to last.
          this.totalSnapshots = response.totalPages * 7;
          this.showPaginator = this.totalPages > 1 && this.currentPage !== this.totalPages;
          this.loading = false;
        }),
    );
  }

  /**
   * Formats the snapshots dates to be displayed.
   * This will need to be replaced with angular datePipes and proper month translations using i18n.
   * https://bulbapp.atlassian.net/browse/BULBAPP-3653
   *
   * @param snapshots list to be formatted.
   */
  formatSnapshots(snapshots) {
    const formattedSnapshots = [];
    for (const snapshot of snapshots) {
      const date = new Date(snapshot.date);
      let localTimeOptions = {};
      localTimeOptions = {
        hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: true,
      };
      const timeAMPM = date.toLocaleString('en-US', localTimeOptions);
      const formattedDate = `${this.months[date.getMonth()]} ${
        date.getDate()}, ${
        date.getFullYear()}, ${
        timeAMPM}`;
      formattedSnapshots.push({ date: formattedDate, id: snapshot.id });
    }
    return formattedSnapshots;
  }

  /**
   * Used to update the page when the current page is changed.
   *
   * @param event the event returned from the on change event. Event contains number of rows requests(7)
   * and "first" the total rows already displayed(page number * 7).
   */
  paginateOnChange(event): void {
    const pageNumber = event.first / this.pageSize;
    this.fetchSnapshotPage(pageNumber);
  }

  /**
   * Navigates user to a single snapshot to preview.
   *
   * @param index of the selected snapshot to preview.
   */
  selectSnapshot(index: number) {
    this.selectedIndex = index;
    this.dynamicDialogRef.close();
    if (this.currentPage === 0 && this.currentSnapshots.length === 1) {
      this.changeState(this.selectedIndex, true, true);
    } else if (this.currentPage === 0 && this.selectedIndex === 0) {
      this.changeState(this.selectedIndex, true, false);
    } else if (this.currentPage === this.totalPages - 1 && this.selectedIndex === this.currentSnapshots.length - 1) {
      this.changeState(this.selectedIndex, false, true);
    } else {
      this.changeState(this.selectedIndex, false, false);
    }
  }

  /**
   * To change the snapshot view of page.
   * @param event
   */
  changeSnapshot = (event) => {
    if (event.detail.changeState === 'PREVIOUS') {
      if (this.currentPage === 0 && this.selectedIndex === 1) {
        this.selectedIndex -= 1;
        this.changeState(this.selectedIndex, true, false);
      } else if (this.currentPage !== 0 && this.selectedIndex === 0) {
        this.currentPage -= 1;
        const params = { page: this.currentPage, size: this.pageSize };
        this.pageSnapshotDialogService.getSnapshotPage(this.draftPageId, params)
          .then((response) => {
            this.currentSnapshots = this.formatSnapshots(response.dtos);
            this.selectedIndex = this.pageSize - 1;
            this.changeState(this.selectedIndex, false, false);
          });
      } else {
        this.selectedIndex -= 1;
        this.changeState(this.selectedIndex, false, false);
      }
    } else if (this.currentPage === (this.totalPages - 1)
        && this.selectedIndex === (this.currentSnapshots.length - 2)) {
      this.selectedIndex += 1;
      this.changeState(this.selectedIndex, false, true);
    } else if (this.currentPage !== (this.totalPages - 1) && this.selectedIndex === (this.pageSize - 1)) {
      this.currentPage += 1;
      const params = { page: this.currentPage, size: this.pageSize };
      this.selectedIndex = 0;
      this.pageSnapshotDialogService.getSnapshotPage(this.draftPageId, params)
        .then((response) => {
          this.currentSnapshots = this.formatSnapshots(response.dtos);
          this.changeState(this.selectedIndex, false, false);
        });
    } else {
      this.selectedIndex += 1;
      this.changeState(this.selectedIndex, false, false);
    }
  };

  /**
   * To change the state of current page.
   *
   * @param index is the index of snapshot record that has to be displayed.
   * @param previous  which returns boolean value for making left angle button disable.
   * @param next which returns boolean value for making right angle buttons disable.
   */
  changeState(index, previous, next) {
    this.state.go('snapshot_preview', {
      draftPageId: this.draftPageId,
      unitPath: this.unitId,
      snapshotId: this.currentSnapshots[index].id,
      snapshots: this.currentSnapshots,
      timestamp: this.currentSnapshots[index].date,
      previous,
      next,
    });
  }

  /**
   * Will close the dialog.
   */
  closeDialog() {
    document.getElementsByTagName('p-dynamicdialog')[0].remove();
    this.dynamicDialogRef.close();
  }

}
